import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { workExperienceService } from '@/lib/api/services/work-experience.service';
import type { Tables, TablesInsert, TablesUpdate } from '@/lib/supabase/supabase-types';

type WorkExperience = Tables<'work_experience'>;
type WorkExperienceInsert = TablesInsert<'work_experience'>;
type WorkExperienceUpdate = TablesUpdate<'work_experience'>;

export function useWorkExperience(freelancerProfileId?: string) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const queryKey = ['work-experience', freelancerProfileId];

  // Fetch work experience entries
  const {
    data: experiences = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      // If no profile ID provided, get current user's experiences via API
      if (!freelancerProfileId) {
        return await workExperienceService.getMyExperiences();
      }

      // Use backend API to get experiences by profile ID
      return await workExperienceService.getExperiencesByProfile(freelancerProfileId);
    },
    enabled: !!freelancerProfileId || !!user?.id,
  });

  // Create experience mutation
  const createExperienceMutation = useMutation({
    mutationFn: async (data: Omit<WorkExperienceInsert, 'freelancer_profile_id'>) => {
      // Use API for creating experiences
      return await workExperienceService.createExperience(data);
    },
    onMutate: async (newExperience) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousExperiences = queryClient.getQueryData<WorkExperience[]>(queryKey) || [];

      // Optimistically update
      const optimisticExperience: WorkExperience = {
        id: `temp-${Date.now()}`,
        freelancer_profile_id: freelancerProfileId!,
        display_order: Math.max(...previousExperiences.map(e => e.display_order), -1) + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        job_title: newExperience.job_title,
        company: newExperience.company,
        start_date: newExperience.start_date,
        end_date: newExperience.end_date || null,
        location: newExperience.location || null,
        description: newExperience.description || null,
      };

      queryClient.setQueryData(queryKey, [...previousExperiences, optimisticExperience]);

      return { previousExperiences, optimisticExperience };
    },
    onError: (error, _newExperience, context) => {
      // Rollback on error
      if (context?.previousExperiences) {
        queryClient.setQueryData(queryKey, context.previousExperiences);
      }
      toast.error('Failed to add work experience');
      console.error('Error creating work experience:', error);
    },
    onSuccess: (data, _variables, context) => {
      // Replace optimistic update with real data
      const previousExperiences = context?.previousExperiences || [];
      const updatedExperiences = previousExperiences.filter(e => e.id !== context?.optimisticExperience.id);
      queryClient.setQueryData(queryKey, [...updatedExperiences, data].sort((a, b) => {
        // Sort by start_date descending, then display_order
        const dateA = new Date(a.start_date).getTime();
        const dateB = new Date(b.start_date).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return a.display_order - b.display_order;
      }));
      toast.success('Work experience added successfully');
    },
  });

  // Update experience mutation
  const updateExperienceMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: WorkExperienceUpdate }) => {
      // Use API for updating experiences
      return await workExperienceService.updateExperience(id, updates);
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousExperiences = queryClient.getQueryData<WorkExperience[]>(queryKey) || [];

      // Optimistically update
      const updatedExperiences = previousExperiences.map(experience =>
        experience.id === id
          ? { ...experience, ...updates, updated_at: new Date().toISOString() }
          : experience
      );

      queryClient.setQueryData(queryKey, updatedExperiences);

      return { previousExperiences };
    },
    onError: (error, _variables, context) => {
      if (context?.previousExperiences) {
        queryClient.setQueryData(queryKey, context.previousExperiences);
      }
      toast.error('Failed to update work experience');
      console.error('Error updating work experience:', error);
    },
    onSuccess: (data) => {
      // Update with real data
      const currentExperiences = queryClient.getQueryData<WorkExperience[]>(queryKey) || [];
      const updatedExperiences = currentExperiences.map(experience =>
        experience.id === data.id ? data : experience
      );
      queryClient.setQueryData(queryKey, updatedExperiences);
      toast.success('Work experience updated successfully');
    },
  });

  // Delete experience mutation
  const deleteExperienceMutation = useMutation({
    mutationFn: async (id: string) => {
      // Use API for deleting experiences
      await workExperienceService.deleteExperience(id);
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });

      const previousExperiences = queryClient.getQueryData<WorkExperience[]>(queryKey) || [];

      // Optimistically remove
      const updatedExperiences = previousExperiences.filter(experience => experience.id !== id);
      queryClient.setQueryData(queryKey, updatedExperiences);

      return { previousExperiences, deletedExperience: previousExperiences.find(e => e.id === id) };
    },
    onError: (error, _id, context) => {
      if (context?.previousExperiences) {
        queryClient.setQueryData(queryKey, context.previousExperiences);
      }
      toast.error('Failed to delete work experience');
      console.error('Error deleting work experience:', error);
    },
    onSuccess: () => {
      toast.success('Work experience deleted successfully');
    },
  });

  return {
    experiences,
    isLoading,
    error,

    // Mutation functions
    createExperience: createExperienceMutation.mutateAsync,
    updateExperience: ({ id, ...updates }: { id: string } & WorkExperienceUpdate) =>
      updateExperienceMutation.mutateAsync({ id, updates }),
    deleteExperience: deleteExperienceMutation.mutateAsync,

    // Mutation states
    isCreating: createExperienceMutation.isPending,
    isUpdating: updateExperienceMutation.isPending,
    isDeleting: deleteExperienceMutation.isPending,
  };
}

